import React, { useContext, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import './Meal.css';
import './MealItemDetail.css';
import Meal from '../../models/meal';
import DateComponent from '../UI/General/DateComponent';
import { Delete, Edit } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { callApi } from '../../api/call-api';
import MealsContext from '../../context/meals-context';
import StarRating from './StarRating';
import HealthRating from './HealthRating';

interface MealProps {
  meal: Meal;
}

const MealItemDetail = (props: MealProps) => {
  const mealEditLink = `/meal-detail/${props.meal.id}/edit`;
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const mealsContext = useContext(MealsContext);
  const navigate = useNavigate();

  const handleClose = () => {
    setDeleteDialog(false);
  };

  const clickDelete = () => {
    setDeleteDialog(true);
  };

  const handleDeleteMeal = async () => {
    setDeleting(true);
    try {
      await callApi(`/meal/${props.meal.id}`, 'DELETE');
      mealsContext.refresh();
      navigate('/your-meals');
    } finally {
      setDeleting(false);
    }
  };

  if (deleteDialog) {
    return (
      <Dialog
        open={deleteDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Opravdu chcete smazat recept?'}</DialogTitle>
        <DialogActions>
          <Button onClick={handleDeleteMeal} disabled={deleting}>
            {deleting ? 'Smazávám...' : 'Ano'}
          </Button>
          <Button onClick={handleClose} autoFocus>
            Ne
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box className="meal-detail">
      <Stack direction="column" spacing={2}>
        {props.meal.image !== '' && <img alt={props.meal.name} className="meal-item__icon" src={props.meal.image} />}
        <div className="meal__description">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <h2 className="meal__description__name" style={{ margin: 0 }}>
              {props.meal.name}
            </h2>
            <Stack direction="row" className="meal-detail__controls">
              <Link to={mealEditLink} className="meal__edit-icon">
                <IconButton color="primary" aria-label="upload picture" component="label">
                  <Edit />
                </IconButton>
              </Link>
              <IconButton onClick={clickDelete} color="primary" aria-label="upload picture" component="label">
                <Delete />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 1 }}>
            <StarRating value={props.meal.tastiness} />
            <Box sx={{ ml: 'auto' }}>
              <HealthRating value={props.meal.healthiness} />
            </Box>
          </Stack>
          {props.meal.preparationTime !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              Doba přípravy: {props.meal.preparationTime} min
            </Typography>
          )}
          {(props.meal.calories !== null ||
            props.meal.protein !== null ||
            props.meal.carbs !== null ||
            props.meal.fat !== null) && (
            <Box sx={{ my: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {props.meal.calories !== null && (
                  <Typography variant="body2">
                    <strong>{props.meal.calories}</strong> kcal
                  </Typography>
                )}
                {props.meal.protein !== null && (
                  <Typography variant="body2">
                    <strong>{props.meal.protein}g</strong> bílkovin
                  </Typography>
                )}
                {props.meal.carbs !== null && (
                  <Typography variant="body2">
                    <strong>{props.meal.carbs}g</strong> sacharidů
                  </Typography>
                )}
                {props.meal.fat !== null && (
                  <Typography variant="body2">
                    <strong>{props.meal.fat}g</strong> tuků
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          {props.meal.ingredients.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 1 }}>
              {props.meal.ingredients.map((ingredient) => (
                <Chip
                  key={ingredient.id}
                  label={ingredient.quantity ? `${ingredient.name} (${ingredient.quantity})` : ingredient.name}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          )}
          <div dangerouslySetInnerHTML={{ __html: props.meal.description }} />
        </div>
        {props.meal.lastCooking !== null && <DateComponent className="meal" date={props.meal.lastCooking} />}
      </Stack>
    </Box>
  );
};

export default MealItemDetail;
